
import React, { useEffect, useState } from 'react';
import { makeStyles, CircularProgress, Typography, Box, Button, Checkbox, Link } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import { useLocation } from 'react-router-dom';
import matchingRoundsAPI, { ApproveMatchesBody, MatchingRoundId, PublicVentureMatch } from '../../api/matching-rounds';
import mentorsAPI, { MentorId, PublicMentor } from '../../api/mentors';
import { SnackMessage } from '../../components/common';
import { COLORS } from '../../theme/variables';
import { useSnackbar } from 'notistack';
import { TenantId } from '../../api/tenants/Tenant';
import ConfirmFounderRecommendationsApprove from '../../components/matching-rounds/confirm-founder-recommendations-approve';
import { useResourceBundles } from '../../contexts/resource-bundles-context';
import { getRoutePath, Pages } from '../../router/constants';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minHeight: '100vh',
    padding: '40px 16px',
    backgroundColor: COLORS.COLOR_ADDITIONAL_GENERAL_BACKGROUND,
    boxSizing: 'border-box',
    '@media (max-width: 800px)': {
      padding: 0,
    },
  },
  block: {
    width: '100%',
    maxWidth: '800px', // Reduced width for single column
    padding: '32px',
    background: '#FFFFFF',
    boxShadow:
      '0px 0px 2px rgba(34, 91, 187, 0.16), 0px 4px 8px rgba(51, 126, 255, 0.04), 0px 8px 16px rgba(51, 126, 255, 0.04)',
    borderRadius: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    boxSizing: 'border-box',
    '@media (max-width: 800px)': {
      padding: '16px',
      borderRadius: 0,
      minHeight: '100vh',
    },
  },
  logo: {
    width: 300,
    marginBottom: 20,
  },
  title: {
    '@media (max-width: 800px)': {
      fontSize: '24px',
    },
  },
  description: {
    color: COLORS.COLOR_TEXT_LIGHTENED_20,
  },
  card: {
    padding: 20,
    background: '#FFFFFF',
    borderRadius: 8,
    border: `1px solid ${COLORS.COLOR_GRAY_LIGHTENED_30}`,
    cursor: 'pointer',
    transition: 'border-color 0.15s, background-color 0.15s',
  },
  cardSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor: '#F5F9FF',
  },
  cardContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
  },
  cardLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    flex: 1,
    minWidth: 0,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    wordBreak: 'break-word',
    lineHeight: 1.4,
    '@media (max-width: 800px)': {
      fontSize: 16,
    },
  },
  cardDescription: {
    display: '-webkit-box',
    '-webkit-line-clamp': 3,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    lineHeight: 1.5,
    color: COLORS.COLOR_TEXT_BASE,
    fontSize: 14,
  },
  fieldRow: {
    fontSize: 13,
    color: COLORS.COLOR_TEXT_BASE,
    lineHeight: 1.4,
    wordBreak: 'break-word',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
  },
  fieldLabel: {
    fontWeight: 600,
    color: COLORS.COLOR_TEXT_BASE,
  },
  multiLineEllipsis: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': 3,
    '-webkit-box-orient': 'vertical',
    wordBreak: 'break-word',
  },
  twoLineEllipsis: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    wordBreak: 'break-word',
    width: 'fit-content',
  },
  loadingContainer: {
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  successBlock: {
    width: 300,
    minHeight: 180,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '16px',
    padding: 20,
    background: '#FFFFFF',
    boxShadow:
      '0px 0px 2px rgba(34, 91, 187, 0.16), 0px 4px 8px rgba(51, 126, 255, 0.04), 0px 8px 16px rgba(51, 126, 255, 0.04)',
  },
  statusIcon: {
    fontSize: 30,
    marginBottom: 20,
  },
  successIcon: {
    color: COLORS.COLOR_GREEN_BASE,
  },
  errorIcon: {
    color: COLORS.COLOR_RED_BASE,
  },
  statusText: {
    textAlign: 'center',
    marginBottom: 16,
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
}));

function FounderMatchApprovePage() {
  const classes = useStyles();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { rb } = useResourceBundles();

  const [status, setStatus] = useState<'loading' | 'error' | 'success' | 'ready'>('loading');
  const [errorDetails, setErrorDetails] = useState<string>('');
  const [matchData, setMatchData] = useState<PublicVentureMatch | null>(null);

  // New State
  const [mentorProfiles, setMentorProfiles] = useState<Map<string, PublicMentor>>(new Map());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Derived from decoded key
  const [urlParams, setUrlParams] = useState<{ tenantId: string, roundId: string, matchId: string } | null>(null);

  const getParseData = (key: string) => {
    try {
      const decoded = atob(key);
      const parts = decoded.split('|');
      if (parts.length >= 3) {
        return {
          tenantId: parts[0],
          roundId: parts[1],
          matchId: parts[2]
        };
      }
    } catch (e) {
      console.error("Failed to decode key", e);
    }
    return null;
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const key = searchParams.get('key');

    if (!key) {
      setStatus('error');
      setErrorDetails('Invalid Link: Missing key.');
      return;
    }

    const params = getParseData(key);
    if (!params) {
      setStatus('error');
      setErrorDetails('Invalid Link: Malformed key.');
      return;
    }

    setUrlParams(params);
    loadData(params.tenantId, params.roundId, params.matchId);
  }, [location.search]);

  const loadData = async (tenantId: string, roundId: string, matchId: string) => {
    try {
      const response = await matchingRoundsAPI.getPublicVentureMatch(
        tenantId as TenantId,
        roundId as MatchingRoundId,
        matchId
      );

      // Assuming the response structure contains matches array and we pick the first one
      // or if it returns the object directly, we need to adjust.
      // Based on API type definition: Promise<PublicMentorMatchesResponse>
      // PublicMentorMatchesResponse has matches: PublicVentureMatch[]

      if (!response) {
        throw new Error('No match data found');
      }

      setMatchData(response);
      const match = response;

      // Fetch Mentor Profiles
      if (match.recommendationMentorIds && match.recommendationMentorIds.length > 0) {
        const profiles = await mentorsAPI.getPublicMentorProfiles(
          tenantId as TenantId,
          match.recommendationMentorIds as MentorId[]
        );
        const profilesMap = new Map<string, PublicMentor>();
        profiles.forEach(p => profilesMap.set(p.id, p));
        setMentorProfiles(profilesMap);
      }

      // Initialize selection based on existing confirmations
      const initialSelected = new Set<string>();
      match.recommendations.forEach(r => {
        if (r.confirmedByFounder) {
          initialSelected.add(r.mentorId);
        }
      });
      setSelectedIds(initialSelected);

      setStatus('ready');

    } catch (e: any) {
      setStatus('error');
      setErrorDetails(e.response?.data?.message || 'Failed to load match.');
    }
  };

  const handleToggle = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleOpenConfirm = () => {
    setIsConfirmModalOpen(true);
  };

  const handleCloseConfirm = () => {
    setIsConfirmModalOpen(false);
  };

  const handleConfirmApprove = async () => {
    if (!urlParams || !matchData) return;

    handleCloseConfirm();
    setIsSubmitting(true);
    try {
      const body = matchData.recommendations.reduce<ApproveMatchesBody>(
        (acc, r) => {
          acc[selectedIds.has(r.mentorId) ? 'approved' : 'rejected'].push(r.id);
          return acc;
        },
        { approved: [], rejected: [] },
      );

      await matchingRoundsAPI.approveVentureMatch(
        urlParams.tenantId as TenantId,
        urlParams.roundId as MatchingRoundId,
        urlParams.matchId,
        body
      );

      setStatus('success');
      setIsSubmitting(false);
    } catch (e: any) {
      setIsSubmitting(false);
      enqueueSnackbar('An error occurred while approving matches', {
        variant: 'error',
        content: (key, message) =>
          SnackMessage({
            key,
            message,
            variant: 'error',
            additionalMessage: e.response?.data?.message || 'Internal server error',
          }),
      });
    }
  };

  if (status === 'loading') {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress size={54} />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className={classes.container}>
        <img src='/Logo.png' alt='logo' className={classes.logo} />
        <div className={classes.successBlock}>
          <CancelIcon className={`${classes.statusIcon} ${classes.errorIcon}`} />
          <Typography variant='h5' className={classes.statusText}>
            Something went wrong.
          </Typography>
          <Typography variant='body1' align='center'>
            {errorDetails}
          </Typography>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className={classes.container}>
        <img src='/Logo.png' alt='logo' className={classes.logo} />
        <div className={classes.successBlock}>
          <CheckCircleIcon className={`${classes.statusIcon} ${classes.successIcon}`} />
          <Typography variant='h5' className={classes.statusText}>
            Matches Approved!
          </Typography>
          <Typography variant='body1' align='center'>
            Thank you for your response. Your approved matches have been recorded.
          </Typography>
        </div>
      </div>
    );
  }

  const selectedMentors = matchData?.recommendations
    .filter(r => selectedIds.has(r.mentorId))
    .map(r => mentorProfiles.get(r.mentorId))
    .filter((p): p is PublicMentor => !!p) || [];

  const unselectedMentors = matchData?.recommendations
    .filter(r => !selectedIds.has(r.mentorId))
    .map(r => mentorProfiles.get(r.mentorId))
    .filter((p): p is PublicMentor => !!p) || [];

  return (
    <div className={classes.container}>
      <img src='/Logo.png' alt='logo' className={classes.logo} />
      <div className={classes.block}>
        <Typography variant='h2' className={classes.title}>
          {matchData?.ventureName}
        </Typography>
        <Typography variant='body1' className={classes.description}>
          Please review the suggested {rb('mentors')} and approve your selections.
        </Typography>

        <div className={classes.cardList}>
          {(!matchData?.recommendations || matchData.recommendations.length === 0) && (
            <Typography variant="body2" style={{ padding: 16, color: COLORS.COLOR_TEXT_LIGHTENED_30, textAlign: 'center' }}>
              No {rb('mentors')} available.
            </Typography>
          )}
          {matchData?.recommendations.map((recommendation) => {
            const isSelected = selectedIds.has(recommendation.mentorId);
            const profile = mentorProfiles.get(recommendation.mentorId);

            return (
              <div
                key={recommendation.mentorId}
                className={`${classes.card} ${isSelected ? classes.cardSelected : ''}`}
                onClick={() => !isSubmitting && handleToggle(recommendation.mentorId)}
                style={isSubmitting ? { opacity: 0.5, pointerEvents: 'none' } : {}}
              >
                <div className={classes.cardContent}>
                  <div className={classes.cardLeft}>
                    <div className={classes.cardTitle}>
                      <Link
                        href={getRoutePath(Pages.MENTOR_DETAILS, { mentorId: recommendation.mentorId })}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e: React.SyntheticEvent) => e.stopPropagation()}
                        color="primary"
                      >
                        {profile ? `${profile.firstName} ${profile.lastName}` : recommendation.mentorName}
                      </Link>
                    </div>

                    {profile?.about && (
                      <div className={classes.cardDescription}>{profile.about}</div>
                    )}

                    {profile?.skills && (
                      <div className={classes.fieldRow}>
                        <span className={classes.fieldLabel}>Skills: </span>
                        {profile.skills}
                      </div>
                    )}
                  </div>
                  <Checkbox
                    checked={isSelected}
                    color="primary"
                    inputProps={{ 'aria-label': `select ${rb('mentor')}` }}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <Box display="flex" justifyContent="flex-end" marginTop={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenConfirm}
            disabled={selectedIds.size === 0 || isSubmitting}
            size="large"
          >
            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Approve Matches'}
          </Button>
        </Box>
      </div>

      <ConfirmFounderRecommendationsApprove
        open={isConfirmModalOpen}
        onClose={handleCloseConfirm}
        onConfirm={handleConfirmApprove}
        selectedMentors={selectedMentors}
        unselectedMentors={unselectedMentors}
      />
    </div>
  );
}

export default FounderMatchApprovePage;
